import { Controller, useFormContext } from 'react-hook-form';
import { Grid, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { Estate_UpdateEstateInput } from 'queries';

import FormDateField from 'common/form-date-field';
import FormInputErrorMessage from 'common/form-input-error-message';
import FormPhoneField from 'common/form-phone-field';
import FormSsnField from 'common/form-ssn-field';
import FormTextField from 'common/form-text-field';
import PortalUsersAutocomplete, {
  UserAutoCompleteOption,
} from 'pages/common/portal/portal-users-autocomplete';

const useStyles = makeStyles(() =>
  createStyles({
    h5: {
      paddingTop: '5px',
    },
    formSection: {
      paddingBottom: '10px',
    },
    inputField: {
      width: '100%',
    },
    inputDropdown: {
      width: '100%',
      backgroundColor: 'white',
      height: '4.5vh',
    },
    inputGrid: {
      paddingRight: '20px',
    },
  }),
);

type Props = {
  portalId: string;
  isEditable: boolean;
};

const EstateDetailTruproxyForm = ({ portalId, isEditable }: Props) => {
  const classes = useStyles();
  const {
    formState: { errors },
    control,
  } = useFormContext<Estate_UpdateEstateInput>();

  return (
    <>
      <div className={classes.formSection}>
        <Typography variant="h5" className={classes.h5}>
          Ward or Principal
        </Typography>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.wardFirstName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="First Name"
                  autoFocus
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  errorMessage={errors.estateTruproxyFields?.wardFirstName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.wardLastName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Last Name"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.wardLastName?.message}
                  disabled={!isEditable}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.wardMiddleName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Middle Name"
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  errorMessage={errors.estateTruproxyFields?.wardMiddleName?.message}
                />
              )}
            />
          </Grid>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.wardSsn"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormSsnField
                  {...field}
                  inputRef={ref}
                  label="SSN"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.wardSsn?.message}
                  disabled={!isEditable}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.wardDateOfBirth"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <FormDateField
                  {...{ value, onChange, name }}
                  label="Date of Birth"
                  errorMessage={
                    (errors.estateTruproxyFields?.wardDateOfBirth?.message as string) || undefined
                  }
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container style={{ display: 'inline' }}>
          <Grid style={{ display: 'inline' }} className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.wardAddressStreet"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address Street"
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  errorMessage={errors.estateTruproxyFields?.wardAddressStreet?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.wardAddressCity"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address City"
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  errorMessage={errors.estateTruproxyFields?.wardAddressCity?.message}
                />
              )}
            />
          </Grid>
          <Grid className={classes.inputGrid} item xs={3} sm={2}>
            <Controller
              name="estateTruproxyFields.wardAddressState"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="State"
                  inputProps={{ maxLength: 80 }}
                  disabled={!isEditable}
                  errorMessage={errors.estateTruproxyFields?.wardAddressState?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={9} sm={4}>
            <Controller
              name="estateTruproxyFields.wardAddressZip"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address Zip"
                  inputProps={{ maxLength: 10 }}
                  errorMessage={errors.estateTruproxyFields?.wardAddressZip?.message}
                  disabled={!isEditable}
                />
              )}
            />
          </Grid>
        </Grid>
      </div>
      <div className={classes.formSection}>
        <Typography variant="h5" className={classes.h5}>
          Guardian or Attorney-in-Fact/Agent
        </Typography>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.clientFirstName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="First Name"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.clientFirstName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.clientLastName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Last Name"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.clientLastName?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.clientEmailAddress"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Email Address"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.clientEmailAddress?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.clientPhoneNumber"
              control={control}
              render={({ field: { name, onBlur, onChange, value } }) => (
                <FormPhoneField
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorMessage={errors.estateTruproxyFields?.clientPhoneNumber?.message}
                  label="Phone Number"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name="estateTruproxyFields.clientAddressStreet"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address Street"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.clientAddressStreet?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.clientAddressCity"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address City"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.clientAddressCity?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name="estateTruproxyFields.clientAddressState"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address State"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.clientAddressState?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="estateTruproxyFields.clientAddressZip"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Address Zip"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.clientAddressZip?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <Controller
              name="estateTruproxyFields.clientRoleWithinEstate"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Attorney-in-fact, Guardian, or Conservator"
                  inputProps={{ maxLength: 80 }}
                  errorMessage={errors.estateTruproxyFields?.clientRoleWithinEstate?.message}
                  helperText="Please write in the role"
                />
              )}
            />
          </Grid>
        </Grid>
      </div>
      <div className={classes.formSection}>
        <Typography variant="h5" className={classes.h5}>
          Add a Point Person
        </Typography>
        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={12}>
            <Controller
              name="assignedUserId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <PortalUsersAutocomplete
                  elementId="assignedUserId"
                  portalId={portalId}
                  selectedUserId={value || ''}
                  handleUserChange={(
                    _elementId: string,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    _e: any,
                    option: UserAutoCompleteOption | null,
                  ) => onChange(option?.id)}
                />
              )}
            />
            {errors.assignedUserId?.message && (
              <FormInputErrorMessage error={errors.assignedUserId?.message} />
            )}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default EstateDetailTruproxyForm;
